import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import graphIcon from '../../../images/icons/graph.svg';
import { selectTabStats, selectStrategyCrumb } from '../../../redux/selectors';
import { setTabStats, setStrategyCrumb } from '../../../redux/actions';

import Graph from '../../base/megatable/graph';
import StatTable from '../../base/megatable/stat-table';
import GranularitiesDropdown from '../../base/dropdowns/granularities-dropdown';
import ColumnsDropdown from '../../base/dropdowns/columns-dropdown';
import ReportDropdown from '../../base/dropdowns/report-dropdown';
import DatePicker from '../../base/date/date-picker';
import Pagination from '../../base/common/pagination';
import { PublisherDropdown } from './multiselect-dropdowns';
import { Granularities } from '../../base/megatable/models';
import { Loader, NoResult } from '../../misc/loader-utils';
import { session } from '../../../services/session';
import { api } from '../../../services/api';
import { dateService } from '../../../services/date';
import { usePublisherList } from '../../../utils/custom-hooks';
import { calculatePagination } from '../../base/common/utils';
import { getTicks, processEditGraphStats } from '../../base/megatable/graph-utils';

import { determineGranularities, getChartOptions, columnFormatters, dataMap } from './utils';
import '../../base/megatable/megatable-graph.scss';
import './strategy-tab.scss';

const maxGraphedRows = 2;
const limit = 100;
const { defaultGranularities } = new Granularities();

function StrategyTab({ strategyId, type, defaultDates, pastDateThreshold, futureLimit }) {
  /* STATE */
  const data = dataMap[type];
  const chartOptions = getChartOptions(type);

  const [firstMount, setFirstMount] = useState(true);
  const [graphStats, setGraphStats] = useState([]);
  const [ticks, setTicks] = useState([]);
  const [graphedRows, setGraphedRows] = useState([]);
  const [dateRange, setDateRange] = useState(defaultDates);
  const [columns, setColumns] = useState(data.columns);
  const [granularities, setGranularities] = useState(
    determineGranularities(dateRange, defaultGranularities),
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [option, setOption] = useState(chartOptions[0]);
  const [showGraph, setShowGraph] = useState(true);
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(true);
  const [showNoResult, setShowNoResult] = useState(false);
  const selectedGranularity = granularities.filter(item => item.selected)[0].name;
  const [pubOptions, setPubOptions] = usePublisherList(session.orgId, true);
  const selectedPubs = pubOptions.reduce((agg, item) => {
    if (item.selected) agg += `${agg ? ',' : ''}${item.name}`;
    return agg;
  }, '');
  const formattedDateRange = {
    date_from: dateRange.startDate.format('YYYY-MM-DD'),
    date_to: dateRange.endDate.format('YYYY-MM-DD'),
  };

  /* REDUX */
  const history = useHistory();
  const dispatch = useDispatch();
  const statKey = `${formattedDateRange.date_from}-${formattedDateRange.date_to},${selectedPubs}`;
  const [backendPage, idxStart, idxEnd] = calculatePagination(page, perPage, limit);
  const [stats, totalItems] = useSelector(state =>
    selectTabStats(state, statKey, backendPage, type, idxStart, idxEnd),
  );
  const crumb = useSelector(state => selectStrategyCrumb(state, type));

  /* EFFECTS */
  useEffect(() => {
    // Redux stats change effect
    if (!stats.length || firstMount) {
      setGranularities(determineGranularities(dateRange, granularities));
      queryTable().then(initialRows => queryGraph(initialRows));
    } else {
      setLoading(false);
    }
  }, [statKey, page, perPage]);

  useEffect(() => {
    // Graph params effect
    if (firstMount) return;
    queryGraph();
  }, [selectedGranularity, graphedRows]);

  /* METHODS */
  function queryGraph(initialRows = []) {
    const rows = graphedRows.length ? graphedRows : initialRows;
    if (!rows.length) return;
    setGraphLoading(true);

    const endpoint = `/api/v0/merchants/${session.orgId}/strategies/${strategyId}/interval_performance/`;
    const gParam = ['daily', 'weekly'].includes(selectedGranularity) ? 'daily' : 'monthly';
    const requests = [];
    rows.forEach(row => {
      requests.push(api.get(endpoint, getGraphParams(row, gParam)));
    });

    Promise.all(requests).then((respArr, i) => {
      const respData = respArr.map((resp, i) => {
        const respStats = resp.data.data[0].stats;
        return {
          data: respStats,
          name: rows[i][data.name_key],
          color: ['green', 'purple'][i],
        };
      });

      const ticks = getTicks(
        selectedGranularity,
        formattedDateRange.date_from,
        formattedDateRange.date_to,
      );
      const graphStats = processEditGraphStats(
        respData,
        ticks,
        selectedGranularity,
        formattedDateRange,
      );
      setGraphStats(graphStats);
      setTicks(ticks);
      setGraphLoading(false);
      setFirstMount(false);
    });
  }

  function queryTable() {
    setLoading(true);
    const endpoint = data.url(session.orgId, strategyId);
    const params = getParams();

    return api
      .get(endpoint, params)
      .then(resp => {
        const tableStats = resp.data.data[0].stats;
        const totalItems = resp.data.data[0].page_info.total_items;

        if (!tableStats.length) {
          setShowNoResult(true);
          setLoading(false);
          setGraphLoading(false);
          return;
        }

        if (firstMount) {
          setGraphedRows(tableStats.slice(0, 2));
        }

        dispatch(setTabStats(tableStats, totalItems, statKey, backendPage, type));
        setLoading(false);
        setShowNoResult(false);
        return tableStats.slice(0, 2);
      })
      .catch(() => {
        setShowNoResult(true);
        setLoading(false);
      });
  }

  function getParams() {
    return {
      date_from: formattedDateRange.date_from,
      date_to: formattedDateRange.date_to,
      ...(selectedPubs ? { pub_ids: selectedPubs } : {}),
      page: backendPage,
      per_page: limit,
    };
  }

  function updateGraphedState(row, add = true) {
    if (graphedRows.length >= maxGraphedRows && add) return;
    if (add) {
      setGraphedRows(graphedRows.concat(row));
    } else {
      const key = data.interval_key;
      setGraphedRows(
        graphedRows.filter(item => {
          return item[key] !== row[key];
        }),
      );
    }
  }

  function getGraphParams(row, granularity) {
    return {
      date_from: formattedDateRange.date_from,
      date_to: formattedDateRange.date_to,
      granularity,
      [data.graph_param_key]: row[data.interval_key],
    };
  }

  function setDates(startDate, endDate) {
    setDateRange({
      startDate: dateService.dateFromStr(startDate),
      endDate: dateService.dateFromStr(endDate),
    });
  }

  /* RENDER */
  const chartOptionsEl = (
    <div className="chart-options">
      {chartOptions.map((item, i) => {
        return (
          <div
            key={`chart-option-${i}`}
            className={`option ${option.statKey === item.statKey ? 'selected' : ''}`}
            onClick={() => setOption(item)}
          >
            {item.label}
          </div>
        );
      })}
    </div>
  );

  const graph =
    showGraph && !graphLoading && !showNoResult && graphedRows.length ? (
      <div className="megatable-graph">
        <Graph
          stats={graphStats}
          xTicks={ticks}
          chartOptions={option}
          dates={formattedDateRange}
          granularity={selectedGranularity}
        />
        <Loader loading={graphLoading} />
        <NoResult
          noResult={!graphedRows.length || showNoResult}
          type="strategy-performance-tab-graph"
        />
      </div>
    ) : null;

  const table =
    !loading && !showNoResult ? (
      <div className="base-megatable graphed tab">
        <StatTable
          stats={stats}
          totals={[]}
          columns={columns}
          sortDisabledFields={[]}
          statType={`strategy-tab-${type}`}
          tableName=""
          sortOptions={{}}
          disableEditUrl={true}
          showTotals={false}
          updateSortParams={() => {}}
          showGraphedRows={true}
          graphedRows={graphedRows}
          maxGraphedRows={maxGraphedRows}
          updateGraphedState={updateGraphedState}
          clearGraphedState={() => setGraphedRows([])}
          graphKey={data.interval_key}
          history={history}
          match={{}}
          location=""
          classOverride="base-search-table overview-table"
          columnFormatter={columnFormatters[type]}
          setStrategyCrumb={(crumb, tab) => dispatch(setStrategyCrumb(crumb, tab))}
        />
        <Pagination
          showPagination={!showNoResult}
          currentPage={page}
          totalItems={totalItems}
          perPage={perPage}
          eventPrefix={`Strategy Tab ${type}`}
          updatePage={val => setPage(val)}
          updatePerPage={val => {
            setPerPage(val);
            setPage(1);
          }}
          reducedPagination={true}
        />
      </div>
    ) : null;

  const numPubsSelected = pubOptions.filter(item => item.selected).length;
  const pubDropdown = data.pub_dropdown ? (
    <PublisherDropdown
      gaEvent={{
        category: `Strategy Tab ${type}`,
        label: 'Publisher Dropdown',
      }}
      items={pubOptions}
      updateOptions={setPubOptions}
      staticFields={[]}
      allSelected={numPubsSelected === pubOptions.length}
      numSelected={numPubsSelected}
      label="Publisher"
    />
  ) : null;

  const crumbEl = crumb ? (
    <div className="breadcrumb">
      for "{crumb.story_name}"
      <span className="remove" onClick={() => dispatch(setStrategyCrumb(null))}>
        <span>x</span>
      </span>
    </div>
  ) : null;

  return (
    <div className={`strategy-tab ${type}`}>
      <div className="title">Top {data.title}</div>
      {crumbEl}
      <div className="menu">
        {chartOptionsEl}
        <div className="menu-container">
          <GranularitiesDropdown
            gaEvent={{
              category: 'Strategy Performance Tabs',
              label: `${data.title} Granularity`,
            }}
            items={granularities}
            updateGranularities={items => setGranularities(items)}
            showGraph={showGraph}
          />
          {pubDropdown}
          <DatePicker
            eventPrefix={`Strategy Performance ${data.title} Date Picker`}
            setDateRange={setDates}
            dateRange={dateRange}
            allowFutureDates={false}
            futureLimit={futureLimit}
            disablePastDates={!!pastDateThreshold}
            pastDateThreshold={pastDateThreshold}
          />
          <ColumnsDropdown
            gaEvent={{
              category: 'Strategy Performance Tabs',
              label: `${data.title} Column`,
            }}
            items={columns}
            updateColumns={columns => setColumns(columns)}
            resetColumns={() => setColumns(data.columns)}
            staticFields={data.static_fields}
          />
          <div
            className={`graph-btn ${showGraph ? 'active' : ''}`}
            onClick={() => setShowGraph(!showGraph)}
          >
            <img src={graphIcon} alt="graph icon" />
          </div>
          <ReportDropdown
            statType={`strategy-tab-${type}`}
            orgId={session.orgId}
            gaEvent={{
              category: `Strategy Tab ${type}`,
              label: 'Export Data',
            }}
            queryParams={getParams()}
            columns={columns}
            currentPage={page}
            perPage={perPage}
            rowCounts={{ visible: stats.length, all: totalItems }}
            disabled={loading}
            reportVersion="V2"
          />
        </div>
      </div>
      {graph}
      {table}
      <Loader loading={loading} />
      <NoResult noResult={showNoResult} type="strategy-performance" />
    </div>
  );
}

export default StrategyTab;
